import React, { useState, useEffect, useRef } from "react"
import classnames from "classnames"
import * as layouts from "layouts"
import * as sessions from "sessions"
import * as metasearch from "metasearch"
import * as uuid from "uuid"
import * as brands from "brands"
import * as api from "./api"
import * as httpx from "httpx"
import * as errors from "errors"
import * as timex from "timex"
import { SearchRequest } from "./brandguard.metrics_pb"
import { Bar, Line } from "charts"
import { card as Card } from "./metric.card"
import * as chart from "./chart"
import * as icons from "icons"
import * as inputs from "inputs"
import * as metricsLayouts from "./layouts"
import * as filters from "./filters"
import { getDaysArray, toDayKey, INFISO, NEGINFISO } from "./dates"

interface dayBucket {
	accuracyRate: number
	falsePositiveCount: number
	falseNegativeCount: number
	totalReviewed: number
	agreementCount: number
}

interface parsed {
	labels: string[]
	days: Map<string, dayBucket>
	totalReviewed: number
	totalAgreement: number
	totalFalsePositive: number
	totalFalseNegative: number
}

function parsedInitial(): parsed {
	return {
		labels: [],
		days: new Map(),
		totalReviewed: 0,
		totalAgreement: 0,
		totalFalsePositive: 0,
		totalFalseNegative: 0,
	}
}

function parse(r: api.ModelAccuracyMetricSearchResponse, dr: metasearch.DateRange, uid: string = uuid.NIL): parsed {
	const data = parsedInitial()

	getDaysArray(new Date(dr.oldest), new Date(dr.newest)).forEach((i) => {
		const day = toDayKey(i)
		data.labels.push(day)
		data.days.set(day, {
			accuracyRate: 0,
			falsePositiveCount: 0,
			falseNegativeCount: 0,
			totalReviewed: 0,
			agreementCount: 0,
		})
	})

	;(r.items || []).forEach((e) => {
		if (uid !== uuid.NIL && e.profile_id !== uid) return
		const day = toDayKey(new Date(e.range!.oldest))
		const bucket = data.days.get(day)
		if (!bucket) return

		const reviewed = Number(BigInt(e.total_reviewed || 0))
		const agreement = Number(BigInt(e.agreement_count || 0))
		const fp = Number(BigInt(e.false_positive_count || 0))
		const fn = Number(BigInt(e.false_negative_count || 0))

		bucket.totalReviewed += reviewed
		bucket.agreementCount += agreement
		bucket.falsePositiveCount += fp
		bucket.falseNegativeCount += fn
		bucket.accuracyRate = bucket.totalReviewed > 0 ? bucket.agreementCount / bucket.totalReviewed : 0

		data.totalReviewed += reviewed
		data.totalAgreement += agreement
		data.totalFalsePositive += fp
		data.totalFalseNegative += fn
	})

	return data
}

export default function Display(): JSX.Element {
	const brand = brands.caching.useCached()
	const bearertoken = sessions.useToken()

	const [loading, setLoading] = useState(true)
	const [cause, setCause] = useState(undefined as JSX.Element | undefined)

	const [req, setReq] = useState(
		api.SearchZero({
			brand_id: brand.id,
			range: metasearch.dateranges.days(30),
		}) as SearchRequest,
	)

	const [chartData, setChartData] = useState(parsedInitial())
	const [currentUser, setCurrentUser] = useState(filters.zeroUser)
	const [response, setResponse] = useState({} as api.ModelAccuracyMetricSearchResponse)
	const currentUserRef = useRef(currentUser)
	currentUserRef.current = currentUser

	const updateCurrentUser = (user: filters.user): void => {
		setCurrentUser(user)
		setChartData(parse(response, req.range!, user.key))
	}

	useEffect(() => {
		if (req.brand_id === uuid.NIL || req.range?.oldest === NEGINFISO || req.range?.newest === INFISO) return
		setLoading(true)
		const retry = httpx.autoretry()
		const p = retry
			.wrap(() => api.search_model_accuracy(req, bearertoken))
			.then((resp) => {
				setResponse(resp)
				setChartData(parse(resp, req.range!, currentUserRef.current.key))
				setCause(undefined)
			})
			.catch(httpx.errors.cancellation(() => undefined))
			.catch(
				httpx.errors.forbidden((cause) => {
					console.warn("insufficient privileges unable to view metrics", cause)
					setCause(
						<layouts.containers.box styled m="auto">
							<errors.Textual>you do not have permission to view metrics</errors.Textual>
						</layouts.containers.box>,
					)
				}),
			)
			.catch((c: unknown) => {
				console.error("unable to retrieve metrics", c)
				setCause(
					<layouts.containers.box styled m="auto" width="100%" height="100%">
						<errors.Textual onClick={() => setCause(undefined)}>unable to retrieve metrics</errors.Textual>
					</layouts.containers.box>,
				)
			})
			.finally(() => setLoading(false))
		return p.cancel
	}, [req])

	const overallAccuracy = chartData.totalReviewed > 0 ? (chartData.totalAgreement / chartData.totalReviewed) * 100 : 0
	const totalDisagreements = chartData.totalFalsePositive + chartData.totalFalseNegative

	const accuracyRates = chartData.labels.map((day) => {
		const bucket = chartData.days.get(day)
		return bucket ? bucket.accuracyRate * 100 : 0
	})

	const fpCounts = chartData.labels.map((day) => {
		const bucket = chartData.days.get(day)
		return bucket ? bucket.falsePositiveCount : 0
	})

	const fnCounts = chartData.labels.map((day) => {
		const bucket = chartData.days.get(day)
		return bucket ? bucket.falseNegativeCount : 0
	})

	const lineOptions = {
		maintainAspectRatio: true,
		scales: {
			x: {
				ticks: { color: layouts.theme.colors.grey.light30 },
				grid: { display: false },
			},
			y: {
				type: "linear" as const,
				display: true,
				position: "left" as const,
				min: 0,
				max: 100,
				ticks: {
					color: layouts.theme.colors.grey.light30,
					maxTicksLimit: 5,
					callback: (value: number | string) => `${value}%`,
				},
				title: {
					display: true,
					text: "Accuracy Rate",
					color: layouts.theme.colors.grey.light30,
					font: { size: 14 },
					padding: 0,
					align: "center" as const,
				},
				grid: { display: false },
			},
		},
		layout: { padding: 20 },
		elements: { point: { radius: 0, pointHoverRadius: 5 } },
		plugins: {
			legend: {
				position: "top" as const,
				align: "end" as const,
				labels: { usePointStyle: true, boxWidth: 50, font: { size: 10, weight: "bold" as const } },
			},
		},
	}

	const lineData = {
		labels: chartData.labels,
		datasets: [
			{
				fill: true,
				label: "Accuracy Rate",
				data: accuracyRates,
				borderColor: layouts.theme.colors.charts.blue.base,
				backgroundColor: layouts.theme.colors.charts.blue.alpha,
				tension: 0.2,
			},
		],
	}

	const barOptions = {
		maintainAspectRatio: true,
		scales: {
			x: {
				ticks: { color: layouts.theme.colors.grey.light30 },
				grid: { display: false },
			},
			y: {
				type: "linear" as const,
				display: true,
				position: "left" as const,
				min: 0,
				ticks: {
					color: layouts.theme.colors.grey.light30,
					maxTicksLimit: 5,
				},
				title: {
					display: true,
					text: "Count",
					color: layouts.theme.colors.grey.light30,
					font: { size: 14 },
					padding: 0,
					align: "center" as const,
				},
				grid: { display: false },
			},
		},
		layout: { padding: 20 },
		plugins: {
			legend: {
				position: "top" as const,
				align: "end" as const,
				labels: { usePointStyle: true, boxWidth: 50, font: { size: 10, weight: "bold" as const } },
			},
		},
	}

	const barData = {
		labels: chartData.labels,
		datasets: [
			{
				label: "False Positives",
				data: fpCounts,
				backgroundColor: layouts.theme.colors.charts.yellow.alpha,
				borderColor: layouts.theme.colors.charts.yellow.base,
				borderWidth: 1,
			},
			{
				label: "False Negatives",
				data: fnCounts,
				backgroundColor: layouts.theme.colors.charts.green.alpha,
				borderColor: layouts.theme.colors.charts.green.base,
				borderWidth: 1,
			},
		],
	}

	const accuracyMetric = chart.parsedMetricZero({
		title: "Overall Accuracy",
		val: Math.round(overallAccuracy * 10) / 10,
		chart: chart.parsedChartZero({
			axisID: "y",
			borderColor: layouts.theme.colors.charts.blue.base,
			backgroundColor: layouts.theme.colors.charts.blue.alpha,
			labels: chartData.labels,
			data: chartData.labels.reduce(
				(acc, day) => {
					acc[day] = chartData.days.get(day)?.accuracyRate ? chartData.days.get(day)!.accuracyRate * 100 : 0
					return acc
				},
				{} as chart.chartData,
			),
		}),
	})

	const reviewedMetric = chart.parsedMetricZero({
		title: "Total Reviewed",
		val: chartData.totalReviewed,
		chart: chart.parsedChartZero({
			axisID: "y",
			borderColor: layouts.theme.colors.charts.green.base,
			backgroundColor: layouts.theme.colors.charts.green.alpha,
			labels: chartData.labels,
			data: chartData.labels.reduce(
				(acc, day) => {
					acc[day] = chartData.days.get(day)?.totalReviewed || 0
					return acc
				},
				{} as chart.chartData,
			),
		}),
	})

	const disagreementsMetric = chart.parsedMetricZero({
		title: "Total Disagreements",
		val: totalDisagreements,
		chart: chart.parsedChartZero({
			axisID: "y",
			borderColor: layouts.theme.colors.charts.yellow.base,
			backgroundColor: layouts.theme.colors.charts.yellow.alpha,
			labels: chartData.labels,
			data: chartData.labels.reduce(
				(acc, day) => {
					const bucket = chartData.days.get(day)
					acc[day] = bucket ? bucket.falsePositiveCount + bucket.falseNegativeCount : 0
					return acc
				},
				{} as chart.chartData,
			),
		}),
	})

	return (
		<layouts.loading.screen loading={loading} height="100%">
			<errors.overlay cause={cause} flexDirection="column" flex="1">
				<layouts.containers.flex>
					<inputs.calendars.DateRangeOneDatepicker
						className={metricsLayouts.StyledDatePicker}
						placeholderText="Date Range"
						minStartDate={new Date(timex.from.iso(req.range?.oldest).minus(timex.duration.days(180)).toISO())}
						maxEndDate={new Date()}
						startDate={new Date(req.range?.oldest || NEGINFISO)}
						endDate={new Date(req.range?.newest || INFISO)}
						changeStart={(d: Date) => {
							setReq((prevState) => ({ ...prevState, range: { newest: INFISO, oldest: timex.from.date(d).toISO() } }))
						}}
						changeEnd={(d: Date) => {
							setReq((prevState) => ({
								...prevState,
								range: { ...prevState.range, newest: timex.from.date(d).toISO() },
							}))
						}}
					/>

					<layouts.containers.flex mt="3px" ml="10px" fontWeight="600">
						{req.range?.oldest !== NEGINFISO &&
							req.range?.newest !== INFISO &&
							`${timex.from
								.iso(req.range?.newest)
								.diff(timex.from.iso(req.range?.oldest), "days")
								.days.toFixed()} day(s)`}
					</layouts.containers.flex>
					<filters.ApprovalFilter
						req={req}
						setReq={setReq}
						currentUser={currentUser}
						setCurrentUser={updateCurrentUser}
					/>
				</layouts.containers.flex>

				<layouts.containers.box
					className="metrics"
					width="100%"
					background={layouts.theme.colors.white}
					overflowY="auto"
				>
					<layouts.containers.box className={classnames("primary", metricsLayouts.hover)} height="512px" maxHeight="512px">
						<Line options={lineOptions} data={lineData} width={100} />
					</layouts.containers.box>

					<layouts.containers.box className={classnames("primary", metricsLayouts.hover)} height="512px" maxHeight="512px" mt="20px">
						<Bar options={barOptions} data={barData} width={100} />
					</layouts.containers.box>

					<layouts.containers.flex justifyContent="center">
						<Card
							icon={
								<icons.ImpressionsEye
									fill={accuracyMetric.chart.borderColor}
									width="30px"
									height="30px"
									verticalAlign="middle"
								/>
							}
							data={accuracyMetric}
						/>
						<Card
							icon={
								<icons.ImpressionsEye
									fill={reviewedMetric.chart.borderColor}
									width="30px"
									height="30px"
									verticalAlign="middle"
								/>
							}
							data={reviewedMetric}
						/>
						<Card
							icon={
								<icons.ImpressionsEye
									fill={disagreementsMetric.chart.borderColor}
									width="30px"
									height="30px"
									verticalAlign="middle"
								/>
							}
							data={disagreementsMetric}
						/>
					</layouts.containers.flex>
				</layouts.containers.box>
			</errors.overlay>
		</layouts.loading.screen>
	)
}
